import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  adminBlogList, adminBlogCreate, adminBlogUpdate, adminBlogDelete,
  adminBlogPublishToggle, adminBlogAiGenerate, adminBlogRenderPreview,
  adminBlogCategories, adminUploadBlogImage,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Newspaper, Plus, Edit2, Trash2, Eye, EyeOff, ExternalLink, Sparkles,
  Loader2, ImageIcon, Link as LinkIcon, Bold, Italic, List, Heading2,
  Quote, Code, Save, ArrowLeft, RefreshCcw, Search, Upload, X as XIcon,
} from 'lucide-react';

const TONES = ['informative', 'conversational', 'authoritative', 'enthusiastic', 'educational'];
const LANGS = [
  { v: 'english', l: 'English' },
  { v: 'bangla',  l: 'Bangla / Banglish' },
  { v: 'hindi',   l: 'Hindi' },
];
const LENGTHS = [
  { v: 'short',  l: 'Short (~600 words)' },
  { v: 'medium', l: 'Medium (~1100 words)' },
  { v: 'long',   l: 'Long (~1800 words)' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso.slice(0, 16).replace('T', ' '); }
}

function StatusBadge({ status }) {
  if (status === 'published') {
    return <Badge className="bg-[hsl(152_78%_25%)] text-[hsl(152_78%_75%)] border-[hsl(152_78%_45%)]/30 hover:bg-[hsl(152_78%_30%)]">Published</Badge>;
  }
  return <Badge variant="outline" className="border-white/10 text-muted-foreground">Draft</Badge>;
}

// =================================================================
// LIST VIEW
// =================================================================
function BlogList({ items, loading, onEdit, onDelete, onPublishToggle, onRefresh, onNew, onAiNew }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((p) =>
      `${p.title || ''} ${p.summary || ''} ${(p.tags || []).join(' ')} ${p.category || ''}`.toLowerCase().includes(s)
    );
  }, [items, q]);

  return (
    <Card className="card-surface" data-testid="blog-list-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Newspaper className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Blog Posts
          {items.length > 0 && <span className="text-xs text-muted-foreground font-normal">· {items.length} total</span>}
        </CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              className="h-9 pl-7 w-[200px] bg-white/[0.02] border-white/10 text-xs" data-testid="blog-search" />
          </div>
          <Button size="sm" variant="outline" onClick={onRefresh} disabled={loading}
            className="h-9 border-white/10 bg-white/[0.02] text-muted-foreground gap-2">
            <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={onAiNew}
            className="h-9 border-[hsl(43_92%_56%)]/40 bg-[hsl(43_92%_56%)]/10 text-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_56%)]/15 gap-2"
            data-testid="blog-ai-new">
            <Sparkles className="h-3.5 w-3.5" /> AI Generate
          </Button>
          <Button size="sm" onClick={onNew} className="h-9 bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_50%)] text-black font-semibold gap-2"
            data-testid="blog-new">
            <Plus className="h-4 w-4" /> New post
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">Cover</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Views</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} data-testid={`blog-row-${p.slug}`}>
                  <TableCell>
                    {p.cover_image ? (
                      <img src={p.cover_image} alt="" className="w-12 h-9 object-cover rounded-sm border border-white/10" />
                    ) : (
                      <div className="w-12 h-9 rounded-sm bg-white/[0.04] border border-white/10 flex items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-3.5 w-3.5" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-sm">{p.title}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">/{p.slug}</div>
                  </TableCell>
                  <TableCell><span className="text-xs text-muted-foreground">{p.category || '—'}</span></TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">{p.views || 0}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(p.updated_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {p.status === 'published' && (
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="outline" className="h-8 px-2 border-white/10 bg-white/[0.02]" title="View live">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        </a>
                      )}
                      <Button size="sm" variant="outline" onClick={() => onPublishToggle(p)}
                        className="h-8 px-2 border-white/10 bg-white/[0.02]" title={p.status === 'published' ? 'Unpublish' : 'Publish'}
                        data-testid={`blog-toggle-${p.slug}`}>
                        {p.status === 'published' ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onEdit(p)}
                        className="h-8 px-2 border-white/10 bg-white/[0.02]" title="Edit"
                        data-testid={`blog-edit-${p.slug}`}>
                        <Edit2 className="h-3.5 w-3.5" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline"
                            className="h-8 px-2 border-[rgba(225,29,46,0.35)] bg-[rgba(225,29,46,0.05)] text-[hsl(0_72%_60%)] hover:bg-[rgba(225,29,46,0.12)]"
                            data-testid={`blog-delete-${p.slug}`} title="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Permanently removes <strong>{p.title}</strong>. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onDelete(p)}
                              className="bg-[hsl(0_72%_50%)] hover:bg-[hsl(0_72%_45%)] text-white">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground text-sm py-10">
                    {items.length === 0
                      ? 'No posts yet. Click "AI Generate" or "New post" to start.'
                      : 'No posts match your search.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// =================================================================
// EDITOR
// =================================================================
const DRAFT_LS_KEY = 'alvee_blog_editor_draft_v1';

function BlogEditor({ initial, categories, onSave, onCancel, onAiAssist }) {
  const isNew = !initial?.id;

  // Restore localStorage autosave on mount for NEW posts only.
  const initialForm = useMemo(() => {
    const base = {
      id: initial?.id || null,
      title: initial?.title || '',
      summary: initial?.summary || '',
      content_md: initial?.content_md || '',
      cover_image: initial?.cover_image || '',
      category: initial?.category || 'Knowledge Base',
      tags: (initial?.tags || []).join(', '),
      seo_title: initial?.seo_title || '',
      seo_description: initial?.seo_description || '',
      slug: initial?.slug || '',
      status: initial?.status || 'draft',
    };
    if (isNew && typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem(DRAFT_LS_KEY);
        if (raw) {
          const saved = JSON.parse(raw);
          // Only restore if base form is empty (no AI-generated initial content)
          if (!base.title && !base.content_md && saved && (saved.title || saved.content_md)) {
            return { ...base, ...saved, id: null };
          }
        }
      } catch { /* ignore */ }
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form, setForm] = useState(initialForm);
  const [previewHtml, setPreviewHtml] = useState(initial?.content_html || '');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingInline, setUploadingInline] = useState(false);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);

  // Keep a ref of the latest form so toolbar buttons never read stale state
  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);

  // Detect if we restored a draft from localStorage (only on first mount, new post)
  useEffect(() => {
    if (isNew && initialForm.title && !initial?.title) {
      setRestoredFromDraft(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function up(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  // Functional updater that uses LATEST state (no stale closure)
  function upFn(updater) { setForm((s) => updater(s)); }

  // ---- Autosave to localStorage every 2s for NEW posts (protect against loss) ----
  useEffect(() => {
    if (!isNew) return;
    const t = setTimeout(() => {
      try {
        const snapshot = { ...form };
        // Only save if there's meaningful content
        if (snapshot.title || snapshot.content_md || snapshot.summary) {
          localStorage.setItem(DRAFT_LS_KEY, JSON.stringify(snapshot));
        }
      } catch { /* ignore quota errors */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [form, isNew]);

  function clearDraft() {
    try { localStorage.removeItem(DRAFT_LS_KEY); } catch { /* ignore */ }
  }

  // Debounced live preview
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!form.content_md) { setPreviewHtml(''); return; }
      setPreviewLoading(true);
      try {
        const r = await adminBlogRenderPreview(form.content_md);
        setPreviewHtml(r.html || '');
      } catch { /* ignore */ }
      finally { setPreviewLoading(false); }
    }, 600);
    return () => clearTimeout(t);
  }, [form.content_md]);

  // FIXED: insertAtCursor now reads CURRENT state via ref (no stale closure)
  // and uses functional state update — eliminates the text-loss bug.
  function insertAtCursor(textareaId, before, after = '') {
    const el = document.getElementById(textareaId);
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    upFn((s) => {
      const current = s.content_md;
      const sel = current.slice(start, end);
      const next = current.slice(0, start) + before + sel + after + current.slice(end);
      return { ...s, content_md: next };
    });
    setTimeout(() => {
      el.focus();
      // Re-fetch latest after state commit
      const latest = formRef.current.content_md;
      const cursor = Math.min(start + before.length + (end - start) + after.length, latest.length);
      el.selectionStart = cursor;
      el.selectionEnd = cursor;
    }, 0);
  }

  // ---- File upload helpers ----
  const coverFileRef = useRef(null);
  const inlineFileRef = useRef(null);

  async function handleCoverUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting same file
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File too large (max 8 MB)');
      return;
    }
    setUploadingCover(true);
    try {
      const r = await adminUploadBlogImage(file);
      up('cover_image', r.url);
      toast.success('Cover image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploadingCover(false); }
  }

  async function handleInlineUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File too large (max 8 MB)');
      return;
    }
    setUploadingInline(true);
    try {
      const r = await adminUploadBlogImage(file);
      const alt = (file.name || 'image').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ');
      const el = document.getElementById('blog-md-textarea');
      const start = el?.selectionStart ?? formRef.current.content_md.length;
      const end = el?.selectionEnd ?? start;
      upFn((s) => {
        const current = s.content_md;
        const insertion = `\n![${alt}](${r.url})\n`;
        return { ...s, content_md: current.slice(0, start) + insertion + current.slice(end) };
      });
      toast.success('Image inserted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploadingInline(false); }
  }

  function insertImageByUrl() {
    const url = window.prompt('Image URL (paste any direct https:// image link):');
    if (!url) return;
    const alt = window.prompt('Alt text (optional, helps SEO):') || '';
    insertAtCursor('blog-md-textarea', `\n![${alt}](${url})\n`);
  }
  function insertLink() {
    const url = window.prompt('Link URL:');
    if (!url) return;
    const text = window.prompt('Link text:') || url;
    insertAtCursor('blog-md-textarea', `[${text}](${url})`);
  }

  async function save(asStatus) {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.content_md.trim()) { toast.error('Content is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        summary: form.summary.trim(),
        content_md: form.content_md,
        cover_image: form.cover_image.trim(),
        category: form.category,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        seo_title: form.seo_title.trim(),
        seo_description: form.seo_description.trim(),
        slug: form.slug.trim(),
        status: asStatus || form.status,
      };
      const saved = form.id
        ? await adminBlogUpdate(form.id, payload)
        : await adminBlogCreate(payload);
      clearDraft(); // success → wipe autosave
      toast.success(`Post ${form.id ? 'updated' : 'created'}: ${saved.title}`);
      onSave(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  }

  function discardRestoredDraft() {
    clearDraft();
    setForm({
      id: null,
      title: '', summary: '', content_md: '', cover_image: '',
      category: 'Knowledge Base', tags: '',
      seo_title: '', seo_description: '', slug: '', status: 'draft',
    });
    setRestoredFromDraft(false);
    toast.info('Restored draft discarded');
  }

  return (
    <div className="space-y-4" data-testid="blog-editor">
      {/* Hidden file inputs for upload */}
      <input ref={coverFileRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} data-testid="blog-cover-file-input" />
      <input ref={inlineFileRef} type="file" accept="image/*" className="hidden" onChange={handleInlineUpload} data-testid="blog-inline-file-input" />

      {restoredFromDraft && (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-md border border-[hsl(43_92%_56%)]/30 bg-[hsl(43_92%_56%)]/8 text-xs" data-testid="blog-draft-restored-banner">
          <span className="text-[hsl(43_92%_72%)]">
            <Save className="h-3.5 w-3.5 inline mr-1.5" />
            Restored your unsaved draft from your last session. Continue editing or discard it.
          </span>
          <button type="button" onClick={discardRestoredDraft} className="text-muted-foreground hover:text-white inline-flex items-center gap-1" data-testid="blog-discard-draft">
            <XIcon className="h-3.5 w-3.5" /> Discard
          </button>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}
          className="h-9 border-white/10 bg-white/[0.02] text-muted-foreground gap-2"
          data-testid="blog-back">
          <ArrowLeft className="h-4 w-4" /> Back to list
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={onAiAssist}
            className="h-9 border-[hsl(43_92%_56%)]/40 bg-[hsl(43_92%_56%)]/10 text-[hsl(43_92%_56%)] gap-2"
            data-testid="blog-ai-assist">
            <Sparkles className="h-4 w-4" /> AI rewrite
          </Button>
          <Button variant="outline" size="sm" onClick={() => save('draft')} disabled={saving}
            className="h-9 border-white/10 bg-white/[0.02] gap-2" data-testid="blog-save-draft">
            <Save className="h-4 w-4" /> Save draft
          </Button>
          <Button size="sm" onClick={() => save('published')} disabled={saving}
            className="h-9 bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_50%)] text-black font-semibold gap-2"
            data-testid="blog-publish">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            {form.status === 'published' ? 'Update post' : 'Publish'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr,360px]">
        {/* MAIN COLUMN */}
        <div className="space-y-4">
          <Card className="card-surface">
            <CardContent className="p-4 space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Title</Label>
                <Input value={form.title} onChange={(e) => up('title', e.target.value)}
                  placeholder="e.g. Is Copy Trading Legit and Safe?"
                  className="mt-1 bg-white/[0.02] border-white/10 text-base font-medium"
                  data-testid="blog-title" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Summary</Label>
                <Textarea value={form.summary} onChange={(e) => up('summary', e.target.value)} rows={2}
                  placeholder="1-2 sentence preview shown on the blog index."
                  className="mt-1 bg-white/[0.02] border-white/10 text-sm" maxLength={300}
                  data-testid="blog-summary" />
              </div>
            </CardContent>
          </Card>

          {/* Editor + preview */}
          <Card className="card-surface">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-sm">Content (Markdown)</CardTitle>
                <div className="flex flex-wrap items-center gap-1">
                  <ToolbarBtn onClick={() => insertAtCursor('blog-md-textarea', '## ')} icon={Heading2} label="Heading" />
                  <ToolbarBtn onClick={() => insertAtCursor('blog-md-textarea', '**', '**')} icon={Bold} label="Bold" />
                  <ToolbarBtn onClick={() => insertAtCursor('blog-md-textarea', '*', '*')} icon={Italic} label="Italic" />
                  <ToolbarBtn onClick={() => insertAtCursor('blog-md-textarea', '\n- ')} icon={List} label="List" />
                  <ToolbarBtn onClick={() => insertAtCursor('blog-md-textarea', '\n> ')} icon={Quote} label="Quote" />
                  <ToolbarBtn onClick={() => insertAtCursor('blog-md-textarea', '`', '`')} icon={Code} label="Code" />
                  <ToolbarBtn onClick={insertLink} icon={LinkIcon} label="Link" testid="blog-tb-link" />
                  <ToolbarBtn onClick={insertImageByUrl} icon={ImageIcon} label="Image by URL" testid="blog-tb-image-url" />
                  <ToolbarBtn
                    onClick={() => inlineFileRef.current?.click()}
                    icon={uploadingInline ? Loader2 : Upload}
                    label="Upload image from device"
                    testid="blog-tb-image-upload"
                    spinning={uploadingInline}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2">
                <Textarea
                  id="blog-md-textarea"
                  value={form.content_md}
                  onChange={(e) => up('content_md', e.target.value)}
                  className="bg-white/[0.02] border-white/10 font-mono text-sm min-h-[500px]"
                  placeholder="## Heading 1&#10;&#10;Body paragraph...&#10;&#10;- Bullet&#10;&#10;[Link text](https://example.com)&#10;&#10;![image alt](https://example.com/photo.jpg)"
                  data-testid="blog-content-md"
                />
                <div className="rounded-md border border-white/10 bg-white/[0.02] p-4 min-h-[500px] max-h-[600px] overflow-y-auto">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-1">
                    Preview {previewLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                  </div>
                  {previewHtml ? (
                    <div className="blog-prose" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                  ) : (
                    <div className="text-xs text-muted-foreground italic">Start typing markdown to see the preview…</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SIDEBAR */}
        <div className="space-y-4">
          <Card className="card-surface">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Cover image</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {form.cover_image ? (
                <div className="relative group">
                  <img src={form.cover_image} alt="" className="w-full aspect-video object-cover rounded-md border border-white/10" />
                  <button
                    type="button"
                    onClick={() => up('cover_image', '')}
                    className="absolute top-2 right-2 inline-flex items-center justify-center h-7 w-7 rounded-full bg-black/70 border border-white/20 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black"
                    title="Remove cover"
                    data-testid="blog-cover-remove"
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => coverFileRef.current?.click()}
                  disabled={uploadingCover}
                  className="w-full aspect-video rounded-md border border-dashed border-white/15 bg-white/[0.02] hover:bg-white/[0.04] hover:border-[hsl(43_92%_56%)]/40 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-white text-xs disabled:opacity-50"
                  data-testid="blog-cover-upload-button"
                >
                  {uploadingCover ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Uploading…</>
                  ) : (
                    <><Upload className="h-5 w-5" /> Click to upload cover image</>
                  )}
                  <span className="text-[10px] text-muted-foreground/70">JPG, PNG, WebP, HEIC · max 8 MB</span>
                </button>
              )}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => coverFileRef.current?.click()}
                  disabled={uploadingCover}
                  className="h-8 border-white/10 bg-white/[0.02] gap-1.5 text-xs flex-1"
                  data-testid="blog-cover-upload"
                >
                  {uploadingCover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {form.cover_image ? 'Replace' : 'Upload'}
                </Button>
              </div>
              <div className="text-[10px] text-muted-foreground">— or paste an image URL —</div>
              <Input value={form.cover_image} onChange={(e) => up('cover_image', e.target.value)}
                placeholder="https://images.unsplash.com/…"
                className="bg-white/[0.02] border-white/10 font-mono text-xs"
                data-testid="blog-cover" />
            </CardContent>
          </Card>

          <Card className="card-surface">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Meta</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Category</Label>
                <Select value={form.category} onValueChange={(v) => up('category', v)}>
                  <SelectTrigger className="mt-1 bg-white/[0.02] border-white/10" data-testid="blog-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Tags (comma-separated)</Label>
                <Input value={form.tags} onChange={(e) => up('tags', e.target.value)}
                  placeholder="forex, beginner, strategy"
                  className="mt-1 bg-white/[0.02] border-white/10 text-sm"
                  data-testid="blog-tags" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Custom slug (optional)</Label>
                <Input value={form.slug} onChange={(e) => up('slug', e.target.value)}
                  placeholder="auto-generated from title"
                  className="mt-1 bg-white/[0.02] border-white/10 font-mono text-xs"
                  data-testid="blog-slug" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-surface">
            <CardHeader className="pb-2"><CardTitle className="text-sm">SEO</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">SEO title</Label>
                <Input value={form.seo_title} onChange={(e) => up('seo_title', e.target.value)}
                  placeholder="Same as title if blank"
                  className="mt-1 bg-white/[0.02] border-white/10 text-sm"
                  maxLength={70} data-testid="blog-seo-title" />
                <div className="text-[10px] text-muted-foreground mt-1">{form.seo_title.length}/70</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Meta description</Label>
                <Textarea value={form.seo_description} onChange={(e) => up('seo_description', e.target.value)} rows={3}
                  placeholder="140-155 chars, contains primary keyword."
                  className="mt-1 bg-white/[0.02] border-white/10 text-sm"
                  maxLength={300} data-testid="blog-seo-desc" />
                <div className="text-[10px] text-muted-foreground mt-1">{form.seo_description.length}/300</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ToolbarBtn({ onClick, icon: Icon, label, testid, spinning }) {
  return (
    <Button type="button" size="sm" variant="outline" onClick={onClick}
      title={label} data-testid={testid}
      className="h-8 px-2 border-white/10 bg-white/[0.02] hover:bg-white/[0.06]">
      <Icon className={`h-3.5 w-3.5 ${spinning ? 'animate-spin' : ''}`} />
    </Button>
  );
}

// =================================================================
// AI GENERATOR DIALOG
// =================================================================
function AiGeneratorDialog({ open, onOpenChange, categories, onGenerated }) {
  const [topic, setTopic] = useState('');
  const [category, setCategory] = useState('Knowledge Base');
  const [tone, setTone] = useState('informative');
  const [language, setLanguage] = useState('english');
  const [length, setLength] = useState('medium');
  const [angle, setAngle] = useState('');
  const [loading, setLoading] = useState(false);

  async function generate() {
    if (!topic.trim()) { toast.error('Topic is required'); return; }
    setLoading(true);
    try {
      const r = await adminBlogAiGenerate({
        topic: topic.trim(), category, tone, language, length, angle: angle.trim() || null,
      });
      toast.success(`Draft generated: "${r.title}"`);
      onGenerated(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Generation failed');
    } finally { setLoading(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-[hsl(240_6%_9%)] border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[hsl(43_92%_56%)]" /> AI Blog Generator
          </DialogTitle>
          <DialogDescription className="text-xs">
            Generates a complete SEO-optimized blog draft using Claude Sonnet 4.5.
            Tuned for the trading-education niche.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Topic / working title *</Label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Is copy trading legit and safe?"
              className="mt-1 bg-white/[0.02] border-white/10"
              data-testid="blog-ai-topic" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1 bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Length</Label>
              <Select value={length} onValueChange={setLength}>
                <SelectTrigger className="mt-1 bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LENGTHS.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="mt-1 bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="mt-1 bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGS.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Specific angle (optional)</Label>
            <Input value={angle} onChange={(e) => setAngle(e.target.value)}
              placeholder="e.g. focus on Bangladesh traders, beginner-only, 2026 update"
              className="mt-1 bg-white/[0.02] border-white/10 text-sm"
              maxLength={200} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="border-white/10 bg-white/[0.02]">Cancel</Button>
          <Button onClick={generate} disabled={loading}
            className="bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_50%)] text-black font-semibold gap-2"
            data-testid="blog-ai-go">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4" /> Generate draft</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =================================================================
// MAIN
// =================================================================
export default function AdminBlogTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | edit
  const [editing, setEditing] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [categories, setCategories] = useState([
    'Knowledge Base', 'Regulation & Safety', 'Trading Strategies', 'Beginner Guides',
    'Platform Reviews', 'Crypto', 'Forex', 'News', 'Bangla',
  ]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminBlogList();
      setItems(d.items || []);
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    adminBlogCategories().then((d) => d?.categories && setCategories(d.categories)).catch(() => { /* noop */ });
  }, [refresh]);

  function startNew() { setEditing(null); setView('edit'); }
  function startEdit(p) { setEditing(p); setView('edit'); }
  async function onPublishToggle(p) {
    try { await adminBlogPublishToggle(p.id); toast.success(p.status === 'published' ? 'Unpublished' : 'Published'); await refresh(); }
    catch { toast.error('Failed'); }
  }
  async function onDelete(p) {
    try { await adminBlogDelete(p.id); toast.success('Deleted'); await refresh(); }
    catch { toast.error('Delete failed'); }
  }

  function onAiGenerated(draft) {
    setEditing({
      id: null,
      title: draft.title,
      summary: draft.summary,
      content_md: draft.content_md,
      content_html: '',
      cover_image: '',
      category: draft.category,
      tags: draft.tags,
      seo_title: draft.seo_title,
      seo_description: draft.seo_description,
      slug: draft.slug,
      status: 'draft',
    });
    setAiOpen(false);
    setView('edit');
  }

  function onSaved() { setView('list'); setEditing(null); refresh(); }

  return (
    <div data-testid="admin-blog-tab">
      {view === 'list' && (
        <BlogList
          items={items}
          loading={loading}
          onEdit={startEdit}
          onDelete={onDelete}
          onPublishToggle={onPublishToggle}
          onRefresh={refresh}
          onNew={startNew}
          onAiNew={() => setAiOpen(true)}
        />
      )}
      {view === 'edit' && (
        <BlogEditor
          key={editing?.id || (editing?.title ? `new-${editing.title.slice(0, 20)}` : 'new')}
          initial={editing}
          categories={categories}
          onSave={onSaved}
          onCancel={() => { setView('list'); setEditing(null); }}
          onAiAssist={() => setAiOpen(true)}
        />
      )}
      <AiGeneratorDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        categories={categories}
        onGenerated={onAiGenerated}
      />
    </div>
  );
}
